import type { ComputedRef, Ref } from 'vue';
import {
  endOfMonth,
  endOfToday,
  endOfWeek,
  endOfYear,
  endOfYesterday,
  startOfDay,
  startOfMonth,
  startOfToday,
  startOfWeek,
  startOfYear,
  startOfYesterday,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import { usePrimeVue } from 'primevue/config';
import {
  computed,
  nextTick,
  ref,
  watch,
} from 'vue';

import type { ISmartFilterColumnDataDate } from '@modules/smart-filters/types';
import { useI18n } from '@modules/i18n/composables';
import { watchImmediate } from '@vueuse/core';

import type {
  FirstDayOfWeek,
  IDateRangeEmits,
  IPeriod,
  IPeriodName,
} from '../types';

export interface DateRangeComposable {
  datePeriodOptions: ComputedRef<IPeriod[]>;
  currentPeriod: Ref<IPeriod | undefined>;
  pickerData: Ref<ISmartFilterColumnDataDate>;
  onPeriodChange(period: IPeriod): void;
  onPickerChange(data: ISmartFilterColumnDataDate): void;
  onSelectChange(itemName?: string): void;
}

export default function useDateRangeWithPresets(
  translationKey: Ref<symbol>,
  defaultPeriodName: Ref<IPeriodName | undefined>,
  emit: IDateRangeEmits,
): DateRangeComposable {
  const today = new Date();

  const { getTranslation } = useI18n();
  const primevue = usePrimeVue();

  const currentPeriod = ref<IPeriod>();
  const pickerData = ref<ISmartFilterColumnDataDate>({ start: null, end: null });

  const firstDayOfWeek = computed(() => (primevue.config.locale?.firstDayOfWeek as FirstDayOfWeek));

  const datePeriodOptions = computed<IPeriod[]>(() => [
    {
      name: 'today',
      label: getTranslation(translationKey.value, 'REPORTS.DATE_RANGE.today').value,
      start: startOfToday(),
      end: endOfToday(),
    },
    {
      name: 'yesterday',
      label: getTranslation(translationKey.value, 'REPORTS.DATE_RANGE.yesterday').value,
      start: startOfYesterday(),
      end: endOfYesterday(),
    },
    {
      name: 'last_7_days',
      label: getTranslation(translationKey.value, 'REPORTS.DATE_RANGE.last_7_days').value,
      start: startOfDay(subDays(today, 7)),
      end: endOfToday(),
    },
    {
      name: 'week',
      label: getTranslation(translationKey.value, 'REPORTS.DATE_RANGE.week').value,
      start: startOfWeek(today, { weekStartsOn: firstDayOfWeek.value }),
      end: endOfWeek(today, { weekStartsOn: firstDayOfWeek.value }),
    },
    {
      name: 'last_week',
      label: getTranslation(translationKey.value, 'REPORTS.DATE_RANGE.last_week').value,
      start: startOfWeek(subWeeks(today, 1), { weekStartsOn: firstDayOfWeek.value }),
      end: endOfWeek(subWeeks(today, 1), { weekStartsOn: firstDayOfWeek.value }),
    },
    {
      name: 'month',
      label: getTranslation(translationKey.value, 'REPORTS.DATE_RANGE.month').value,
      start: startOfMonth(today),
      end: endOfMonth(today),
    },
    {
      name: 'last_month',
      label: getTranslation(translationKey.value, 'REPORTS.DATE_RANGE.last_month').value,
      start: startOfMonth(subMonths(today, 1)),
      end: endOfMonth(subMonths(today, 1)),
    },
    {
      name: 'year',
      label: getTranslation(translationKey.value, 'REPORTS.DATE_RANGE.year').value,
      start: startOfYear(today),
      end: endOfYear(today),
    },
  ]);

  watch(datePeriodOptions, () => {
    if (currentPeriod.value?.name === 'last_week' || currentPeriod.value?.name === 'week') {
      const period = datePeriodOptions.value.find((item) => item.name === currentPeriod.value?.name);

      if (period) {
        void nextTick().then(() => onPeriodChange(period));
      }
    }
  });

  watchImmediate(defaultPeriodName, (defaultPeriod) => {
    const period = datePeriodOptions.value.find((item) => item.name === defaultPeriod);

    if (period) {
      onPeriodChange(period);
    }
  });

  function emitChange(): void {
    emit('change', { ...pickerData.value });
  }

  function onPeriodChange(period: IPeriod): void {
    currentPeriod.value = period;
    pickerData.value = {
      start: period.start,
      end: period.end,
    };
    emitChange();
  }

  function onPickerChange(data: ISmartFilterColumnDataDate): void {
    pickerData.value = data;
    currentPeriod.value = undefined;
    emitChange();
  }

  function onSelectChange(itemName?: string): void {
    const item = datePeriodOptions.value.find(({ name }) => itemName === name);

    if (item) {
      onPeriodChange(item);
    }
  }

  return {
    datePeriodOptions,
    currentPeriod,
    pickerData,
    onPeriodChange,
    onPickerChange,
    onSelectChange,
  };
}
